<template lang="pug">
  section#forensic-diagnosis
    .fd-hero.py-5.px-3.px-md-5
      .container.fd-container
        .row.justify-content-center
          .col-12.text-center
            h1.fd-title.mb-3
              span DIAGNÓSTICO SOBRE LOS CUERPOS
              br
              span.fd-title-accent AL RESGUARDO DEL ESTADO

            .fd-lead.light-font.mb-4
              p
                | En México, miles de familias han tenido que asumir la búsqueda de sus seres queridos desaparecidos, una tarea que por ley corresponde al Estado. Entre esas labores se encuentra la más dolorosa:
                | #[span.fd-lead-bold escarbar la tierra con herramientas propias en busca de sus seres queridos desaparecidos.]
              p
                | Nuestro país no tiene un registro público confiable de fosas clandestinas, mucho menos un registro de fosas comunes, donde también podrían localizar a personas reportadas como desaparecidas. A esto se suma la opacidad estatal sobre el destino de los cuerpos recuperados, su resguardo y su identificación forense.
              p
                | Al igual que las fosas clandestinas, las #[span.fd-lead-bold fosas comunes en México] representan una de las manifestaciones más graves de la crisis de derechos humanos, violencia e impunidad en el país. Estas fosas no solo tienen implicaciones legales y sociales muy profundas, sino que también suelen estar marcadas por múltiples #[span.fd-lead-bold malas prácticas estatales] que agravan la situación y vulneran aún más los derechos de las víctimas y sus familias.

            a.fd-cta.mx-auto(
              :href="downloadUrl"
              target="_blank"
              rel="noopener"
            )
              .fd-cta-title.bold-font Descarga y consulta
              .fd-cta-copy
                span.bold-font Cuerpos bajo el resguardo del Estado,
                br
                span.light-font diagnóstico sobre el abandono forense en México


    .fd-section.py-5.px-3.px-md-5
      .container.fd-container
        .row.justify-content-center
          .col-12.col-lg-9
            p.fd-paragraph.light-font
              | La incertidumbre de las familias comienza con la desaparición de un ser querido, pero ésta se agrava cuando el Estado mexicano pierde los cuerpos que localiza, los resguarda sin registro o los abandona en morgues y panteones ministeriales. Esta #[span.fd-paragraph-bold segunda desaparición] profundiza el dolor de miles de personas que esperan justicia y verdad.
            p.fd-paragraph.light-font
              | México atraviesa un desastre forense sin precedentes. La saturación de morgues, la falta de coordinación entre instituciones y la ausencia de un sistema unificado de registros han hecho imposible identificar a miles de personas reportadas como desaparecidas. Las irregularidades, negligencias y contradicciones en las cifras oficiales han minado aún más la confianza de la sociedad. Algunas organizaciones como #[span.fd-paragraph-bold Quinto Elemento Lab] y #[span.fd-paragraph-bold A dónde van los desaparecidos] revelaron que, hasta 2023, hay más de 72 mil cuerpos sin identificar.
            p.fd-paragraph.light-font.mb-0
              | El aumento de la violencia y la falta de una estrategia integral agravan la crisis forense, perpetuando el ciclo de impunidad. Lo que se denomina “crisis forense” es, en realidad, una crisis estructural del Estado mexicano: la incapacidad de garantizar justicia, verdad y dignidad a las personas desaparecidas y a sus familias.

    .fd-section.py-5.px-3.px-md-5
      .container.fd-container
        .row.justify-content-center
          .col-12.text-center
            h2.fd-section-title.mb-3 DESENTERRANDO LA VERDAD: DIBUJAR EL OLVIDO
        .row.justify-content-center
          .col-12.col-lg-9
            p.fd-paragraph.light-font
              | La documentación forense es esencial en contextos de desaparición y violencia sistemática. Permite registrar
              | de forma rigurosa los hallazgos de cuerpos, restos y objetos personales, contribuye a la identificación de las
              | víctimas, respalda investigaciones judiciales y garantiza que las pruebas no sean alteradas ni descartadas.
            p.fd-paragraph.light-font
              | En este esfuerzo, los colectivos de búsqueda - integrados en su mayoría por familiares - han desempeñado un papel clave.
              | A pesar de la falta de apoyo institucional, han desarrollado herramientas propias de registro: cuadernos de campo,
              | bases de datos y mapas. Su conocimiento del territorio y de las dinámicas locales complementa el trabajo técnico con
              | una dimensión humana que el Estado suele ignorar. Aunque su sistematización no sigue un protocolo científico formal,
              | tiene un valor probatorio y ético para la búsqueda de verdad, memoria y justicia.
            p.fd-paragraph.light-font
              | Un ejemplo claro es el colectivo #[span.fd-paragraph-bold Regresando a Casa Morelos] que ha documentado con rigor sus
              | hallazgos en fosas comunes, incluso cuando las autoridades negaban la existencia de restos. Su labor ha sido crucial para
              | evidenciar la negligencia institucional en los procesos de exhumación e identificación forense, así como para impulsar
              | nuevas prácticas de documentación.
            p.fd-paragraph.light-font
              | Articular el conocimiento de estos colectivos con el trabajo forense fortalece los procesos de búsqueda, dignifica a las
              | víctimas y permite avanzar hacia la verdad y la justicia. Escuchar a quienes han buscado durante años no solo es un acto
              | de reconocimiento, es una herramienta fundamental para que las desapariciones no queden en el olvido.
            p.fd-paragraph.light-font
              | A continuación podrás conocer la experiencia de documentación del colectivo #[span.fd-paragraph-bold Regresando a Casa Morelos],
              | que desde 2013 han desempeñado un papel crucial como observadores y documentalistas durante las exhumaciones en fosas comunes
              | oficiales, como las de Tetelcingo y Jojutla, en el estado de Morelos.
            p.fd-paragraph.light-font
              | En 2017, el colectivo ingresó —en grupos de tres a cinco personas— a la “zona cero” de la fosa de Jojutla. Ante la
              | negativa de las autoridades de permitirles contar con peritos independientes, reemplazaron el registro fotográfico formal
              | por anotaciones detalladas, escucha atenta y dibujos fieles de lo observado —desde tatuajes hasta prendas con identificadores
              | claros—. Así construyeron una memoria visual y documental vital, hoy mostrada aquí.
            p.fd-paragraph.light-font
              | Se trata de 19 tarjetas de identificación ilustrativas que permiten a otras familias identificar posibles coincidencias
              | con sus seres queridos desaparecidos. Éstas fueron resultado de la sistematización de la base de datos
              | independiente y accesible del colectivo, que representa un archivo autónomo de justicia viviente capaz de contrarrestar la
              | narrativa oficial, pero sobre todo de evidenciar la negligencia institucional en los entierros irregulares como Jojutla y
              | Tetelcingo, Morelos.
            p.fd-paragraph.light-font
              | Estas tarjetas de identificación son parte de la campaña de impacto del documental Volverte a ver (2020), dirigido por
              | Carolina Corral Paredes y producido por Magali Rocha Donnadieu. En él se retrata la lucha de tres mujeres buscadoras en
              | Morelos —Tranquilina Hernández, Angélica Rodríguez y Edith Hernández— quienes
              | se entrenan como peritas forenses para participar en la exhumación de más de 200 cuerpos enterrados de forma irregular por
              | la Fiscalía del Estado de Morelos. A través de su testimonio y participación directa, el documental expone la crisis forense
              | y de desaparición en México, denunciando la negligencia institucional y mostrando cómo, entre el dolor y la esperanza, los
              | colectivos luchan por rescatar no solo cuerpos, sino también la memoria y la dignidad de sus seres queridos.

        .fd-cards-grid.mt-4
          figure.fd-card(
            v-for="(p, idx) in people"
            :key="(p && p.id) ? p.id : idx"
            :class="{ 'is-hovered': isHovered(p) }"
          )
            button.fd-card-btn(
              type="button"
              :aria-label="`Abrir Persona #${(p && p.id) ? p.id : (idx + 1)}`"
              @click="openCard(p, idx)"
              @mouseenter="setHover(p)"
              @mouseleave="clearHover"
              @focus="setHover(p)"
              @blur="clearHover"
            )
              img.fd-card-img(
                :src="cardSrc(p)"
                :alt="`Persona #${(p && p.id) ? p.id : (idx + 1)}`"
                loading="lazy"
              )

        b-modal.fd-card-modal(
          v-model="isCardModalOpen"
          centered
          hide-header
          hide-footer
          size="xl"
          dialog-class="fd-card-modal-dialog"
          content-class="fd-card-modal-content"
          body-class="fd-card-modal-body"
          @hidden="clearActiveCard"
        )
          .fd-card-modal-inner
            img.fd-card-modal-img(
              :src="activeCardImage"
              :alt="activeCardAlt"
            )
            .fd-card-modal-nav(v-if="hasMultipleCards")
              button.fd-card-modal-flip(type="button" @click="toggleReverse")
                img.fd-card-modal-flip-icon(
                  :src="flipIconSrc"
                  :alt="flipIconAlt"
                )
              .fd-card-modal-nav-center
                button.fd-card-modal-nav-btn(
                  type="button"
                  :disabled="!canNavigate"
                  aria-label="Anterior"
                  @click="prevCard"
                )
                  img.fd-card-modal-nav-icon(
                    :src="arrowLeftIcon"
                    alt=""
                    aria-hidden="true"
                  )
                  span.fd-card-modal-nav-text Anterior
                button.fd-card-modal-nav-btn(
                  type="button"
                  :disabled="!canNavigate"
                  aria-label="Siguiente"
                  @click="nextCard"
                )
                  span.fd-card-modal-nav-text Siguiente
                  img.fd-card-modal-nav-icon(
                    :src="arrowRightIcon"
                    alt=""
                    aria-hidden="true"
                  )
              button.fd-card-modal-close(
                type="button"
                @click="isCardModalOpen = false"
              ) Cerrar

    .fd-section.py-5.px-3.px-md-5
      .container.fd-container
        .row.justify-content-center
          .col-12
            .fd-actions-block
              h2.fd-section-title.mb-0 ¿Qué le toca al Estado mexicano?
              p.fd-paragraph.fd-actions-intro
                | Desde #[span.fd-actions-strong Artículo 19, Data Cívica y el Programa de Derechos Humanos de la IBERO] proponemos seis acciones urgentes
                | para avanzar en la identificación de personas desaparecidas:
              ol.fd-actions
                li.fd-action-item(v-for="(a, idx) in actions" :key="idx")
                  .fd-action-pill
                    span.fd-action-index(v-text="idx + 1")
                    span.fd-action-text(v-text="a")

    .fd-doc.py-5.px-3.px-md-5
      .container.fd-container
        .row.justify-content-center
          .col-12.text-center
            h2.fd-section-title.mb-3 VOLVERTE A VER | DOCUMENTAL
        .row.justify-content-center.mt-4
          .col-12.col-lg-10
            .fd-video
              iframe(
                :src="documentaryEmbedUrl"
                title="Volverte a ver | Documental"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              )
</template>

<script>
const reverseImage = require("@/assets/cards/Reverso.jpg");
const flipIconOn = require("@/assets/cards/voltear-on.png");
const flipIconOff = require("@/assets/cards/voltear-off.png");
const arrowLeftIcon = require("@/assets/cards/arrow_left_alt.svg");
const arrowRightIcon = require("@/assets/cards/arrow_right_alt.svg");

const defaultPeople = [
  {
    id: 5,
    imageOff: require("@/assets/cards/Frente-5-00 off.png"),
    imageOn: require("@/assets/cards/Frente-5-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-5-02 open.jpg")
  },
  {
    id: 7,
    imageOff: require("@/assets/cards/Frente-7-00 off.png"),
    imageOn: require("@/assets/cards/Frente-7-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-7-02 open.jpg")
  },
  {
    id: 12,
    imageOff: require("@/assets/cards/Frente-12-00 off.png"),
    imageOn: require("@/assets/cards/Frente-12-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-12-02 open.jpg")
  },
  {
    id: 15,
    imageOff: require("@/assets/cards/Frente-15-00 off.png"),
    imageOn: require("@/assets/cards/Frente-15-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-15-02 open.jpg")
  },
  {
    id: 16,
    imageOff: require("@/assets/cards/Frente-16-00 off.png"),
    imageOn: require("@/assets/cards/Frente-16-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-16-02 open.jpg")
  },
  {
    id: 18,
    imageOff: require("@/assets/cards/Frente-18-00 off.png"),
    imageOn: require("@/assets/cards/Frente-18-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-18-02 open.jpg")
  },
  {
    id: 24,
    imageOff: require("@/assets/cards/Frente-24-00 off.png"),
    imageOn: require("@/assets/cards/Frente-24-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-24-02 open.jpg")
  },
  {
    id: 30,
    imageOff: require("@/assets/cards/Frente-30-00 off.png"),
    imageOn: require("@/assets/cards/Frente-30-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-30-02 open.jpg")
  },
  {
    id: 32,
    imageOff: require("@/assets/cards/Frente-32-00 off.png"),
    imageOn: require("@/assets/cards/Frente-32-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-32-02 open.jpg")
  },
  {
    id: 35,
    imageOff: require("@/assets/cards/Frente-35-00 off.png"),
    imageOn: require("@/assets/cards/Frente-35-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-35-02 open.jpg")
  },
  {
    id: 36,
    imageOff: require("@/assets/cards/Frente-36-00 off.png"),
    imageOn: require("@/assets/cards/Frente-36-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-36-02 open.jpg")
  },
  {
    id: 37,
    imageOff: require("@/assets/cards/Frente-37-00 off.png"),
    imageOn: require("@/assets/cards/Frente-37-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-37-02 open.jpg")
  },
  {
    id: 38,
    imageOff: require("@/assets/cards/Frente-38-00 off.png"),
    imageOn: require("@/assets/cards/Frente-38-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-38-02 open.jpg")
  },
  {
    id: 47,
    imageOff: require("@/assets/cards/Frente-47-00 off.png"),
    imageOn: require("@/assets/cards/Frente-47-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-47-02 open.jpg")
  },
  {
    id: 50,
    imageOff: require("@/assets/cards/Frente-50-00 off.png"),
    imageOn: require("@/assets/cards/Frente-50-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-50-02 open.jpg")
  },
  {
    id: 51,
    imageOff: require("@/assets/cards/Frente-51-00 off.png"),
    imageOn: require("@/assets/cards/Frente-51-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-51-02 open.jpg")
  },
  {
    id: 56,
    imageOff: require("@/assets/cards/Frente-56-00 off.png"),
    imageOn: require("@/assets/cards/Frente-56-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-56-02 open.jpg")
  },
  {
    id: 73,
    imageOff: require("@/assets/cards/Frente-73-00 off.png"),
    imageOn: require("@/assets/cards/Frente-73-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-73-02 open.jpg")
  },
  {
    id: 77,
    imageOff: require("@/assets/cards/Frente-77-00 off.png"),
    imageOn: require("@/assets/cards/Frente-77-01 on.png"),
    imageOpen: require("@/assets/cards/Frente-77-02 open.jpg")
  }
];

export default {
  name: "ForensicDiagnosis",
  props: {
    downloadUrl: {
      type: String,
      default: "/assets/diagnostico-forense.pdf"
    },
    secondaryLinkUrl: {
      type: String,
      default: "#"
    },
    documentaryEmbedUrl: {
      type: String,
      default: "https://www.youtube.com/embed/WeVp6oKiszI?si=7oJv7DshWIZVdaHh"
    },
    peopleOverride: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      hoveredId: null,
      isCardModalOpen: false,
      activePerson: null,
      activeIndex: 0,
      isReverse: false
    };
  },
  computed: {
    hasMultipleCards() {
      return this.people.length > 1;
    },
    canNavigate() {
      return this.hasMultipleCards && !!this.activePerson;
    },
    people() {
      const rawPeople =
        Array.isArray(this.peopleOverride) && this.peopleOverride.length
          ? this.peopleOverride
          : defaultPeople;

      return rawPeople.map((person, index) => {
        const base = person || {};
        const id = base.id != null ? base.id : index + 1;
        const imageOff = base.imageOff || base.image || null;
        const imageOn = base.imageOn || base.image || imageOff;
        const imageOpen = base.imageOpen || base.image || imageOff;

        return {
          ...base,
          id,
          imageOff,
          imageOn,
          imageOpen
        };
      });
    },
    activeCardImage() {
      if (!this.activePerson) {
        return "";
      }
      if (this.isReverse) {
        return reverseImage;
      }
      return (
        this.activePerson.imageOpen ||
        this.activePerson.image ||
        this.activePerson.imageOff ||
        ""
      );
    },
    activeCardAlt() {
      if (!this.activePerson) {
        return "";
      }
      const id =
        this.activePerson.id != null ? String(this.activePerson.id) : "";
      const label = id ? `Persona #${id}` : "Persona";
      return this.isReverse ? `${label} - Reverso` : label;
    },
    flipIconSrc() {
      return this.isReverse ? flipIconOff : flipIconOn;
    },
    flipIconAlt() {
      return this.isReverse ? "Mostrar frente" : "Mostrar reverso";
    },
    arrowLeftIcon() {
      return arrowLeftIcon;
    },
    arrowRightIcon() {
      return arrowRightIcon;
    },
    actions() {
      return [
        "Creación de un registro centralizado y público de todos los cuerpos bajo el resguardo del Estado: en Servicios Médicos Forenses (Semefos), fosas comunes, panteones ministeriales y demás sitios de resguardo administrados por el Estado.",
        "Establecimiento de un banco de datos forenses transparente, que permita a las familias acceder a la información en tiempo real.",
        "Garantizar el acceso inmediato a huellas dactilares y perfiles genéticos como parte del proceso de identificación.",
        "Mejorar la coordinación entre la CNB, las fiscalías estatales y Semefos para evitar la pérdida, duplicación o abandono de cuerpos en el sistema.",
        "Asignación de mayor presupuesto y personal especializado en identificación humana, incluyendo equipos con experiencia en genética, antropología forense y análisis documental.",
        "Implementación de buenas prácticas impulsadas por los colectivos de búsqueda, reconociendo su aporte técnico y ético en los procesos de identificación."
      ];
    }
  },
  methods: {
    setHover(person) {
      this.hoveredId = person && person.id != null ? person.id : null;
    },
    clearHover() {
      this.hoveredId = null;
    },
    isHovered(person) {
      return person && this.hoveredId === person.id;
    },
    cardSrc(person) {
      if (!person) {
        return "";
      }
      const off = person.imageOff || person.image || "";
      const on = person.imageOn || person.image || off;
      return this.isHovered(person) ? on : off;
    },
    openCard(person, index) {
      const nextIndex =
        typeof index === "number"
          ? index
          : this.people.findIndex(item =>
              person && item ? item.id === person.id : false
            );
      this.activeIndex = nextIndex >= 0 ? nextIndex : 0;
      this.activePerson = person || this.people[this.activeIndex] || null;
      this.isReverse = false;
      this.isCardModalOpen = true;
    },
    clearActiveCard() {
      this.activePerson = null;
      this.activeIndex = 0;
      this.isReverse = false;
    },
    toggleReverse() {
      this.isReverse = !this.isReverse;
    },
    setActiveByIndex(index) {
      if (!this.people.length) {
        return;
      }
      let nextIndex = index;
      if (nextIndex < 0) {
        nextIndex = this.people.length - 1;
      }
      if (nextIndex >= this.people.length) {
        nextIndex = 0;
      }
      const nextPerson = this.people[nextIndex];
      if (!nextPerson) {
        return;
      }
      this.activeIndex = nextIndex;
      this.activePerson = nextPerson;
      this.isReverse = false;
    },
    prevCard() {
      this.setActiveByIndex(this.activeIndex - 1);
    },
    nextCard() {
      this.setActiveByIndex(this.activeIndex + 1);
    }
  }
};
</script>

<style lang="scss">
@import "@/global.scss";

$accent: #ba292f;
$paper: rgba(255, 255, 255, 0.92);
$chalk: rgba(255, 255, 255, 0.85);
$line: rgba(255, 255, 255, 0.18);

#forensic-diagnosis {
  background: #191a1a;
  color: $white;
  text-align: center;
  position: relative;

  .fd-container {
    max-width: 1050px;
  }

  .fd-hero {
    position: relative;
    overflow: hidden;

    background: radial-gradient(
        1200px 500px at 50% 0%,
        rgba(255, 255, 255, 0.06),
        transparent 60%
      ),
      radial-gradient(
        900px 400px at 10% 30%,
        rgba(255, 255, 255, 0.04),
        transparent 55%
      ),
      radial-gradient(
        900px 400px at 90% 40%,
        rgba(255, 255, 255, 0.03),
        transparent 55%
      );

    .fd-title {
      color: #ba292f;
      text-align: center;
      font-family: "Roboto Mono", monospace;
      font-size: 42.28px;
      font-style: normal;
      font-weight: 500;
      line-height: 42px;
      margin: 0 auto;
      max-width: 900px;

      .fd-title-accent {
        color: #ba292f;
      }
    }

    .fd-lead {
      max-width: 900px;
      margin: 0 auto;
      color: #fff;
      text-align: center;
      font-family: "Roboto Mono", monospace;
      font-size: 14px;
      font-style: normal;
      font-weight: 300;
      line-height: 24px;

      p {
        margin: 0 0 24px;
      }

      p:last-child {
        margin-bottom: 0;
      }

      .fd-lead-bold {
        color: #fff;
        font-family: "Roboto Mono", monospace;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px;
      }
    }
  }

  .fd-cta {
    display: flex;
    flex-direction: column;
    width: 500px;
    max-width: 100%;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin: 0 auto;
    border-radius: 20px;
    border: 1.5px solid #ba292f;
    background: transparent;
    text-decoration: none !important;
    color: #fff !important;
    cursor: pointer;

    .fd-cta-title {
      color: #fff;
      text-align: center;
      font-family: "Roboto Mono", monospace;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    }

    .fd-cta-copy {
      display: block;
      text-align: center;
      font-family: "Roboto Serif", serif;
      font-size: 14px;
      font-style: italic;
      font-weight: 400;
      line-height: 1.2;
    }

    span {
      color: inherit;
      font-family: inherit;
      font-size: inherit;
      font-style: inherit;
      font-weight: inherit;
      line-height: inherit;
    }
  }

  .fd-cta,
  .fd-cta:link,
  .fd-cta:visited,
  .fd-cta:hover,
  .fd-cta:active,
  .fd-cta * {
    cursor: pointer;
  }

  .fd-mini-link {
    a {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.65);
      text-decoration: none;
      border-bottom: 1px dashed rgba(255, 255, 255, 0.35);
      padding-bottom: 2px;

      &:hover {
        color: rgba(255, 255, 255, 0.85);
        border-bottom-color: rgba(255, 255, 255, 0.6);
      }
    }
  }

  .fd-section {
    background: $black;
  }

  .fd-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
    margin: 0 auto;
    max-width: 900px;
  }

  .fd-section-title {
    color: #ba292f;
    text-align: center;
    font-family: "Roboto Mono", monospace;
    font-size: 19px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  .fd-paragraph {
    max-width: 900px;
    margin: 0 auto 14px;
    color: #fff;
    font-family: "Roboto Mono", monospace;
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px;
    text-align: center;

    .fd-paragraph-bold {
      color: #fff;
      font-family: "Roboto Mono", monospace;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
    }
  }

  .fd-cards-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    margin: 0 auto;

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 575px) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  .fd-card {
    margin: 0;
    padding: 8px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.65);
    background: rgba(0, 0, 0, 0.28);
  }

  .fd-card-btn {
    width: 100%;
    display: block;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    border-radius: 10px;
    transition: background 120ms ease;
  }

  .fd-card-btn:focus-visible {
    outline: 2px solid rgba(186, 41, 47, 0.7);
    outline-offset: 4px;
    border-radius: 12px;
  }

  .fd-card-img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 10px;
    background: transparent;
  }

  .fd-card.is-hovered .fd-card-img {
    background: #ffffff;
  }

  .fd-actions-block {
    display: flex;
    width: 699px;
    max-width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 50px;
    margin: 0 auto;
    text-align: center;
  }

  .fd-actions-intro {
    width: 675px;
    max-width: 100%;
    margin: 0;
  }

  .fd-actions-strong {
    font-weight: 700;
  }

  .fd-actions {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 675px;
    max-width: 100%;

    .fd-action-item {
      margin-bottom: 0;
    }

    .fd-action-pill {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1.5px solid #ba292f;
      text-align: center;
    }

    .fd-action-index {
      width: 20px;
      color: #ba292f;
      text-align: center;
      font-family: "Roboto Mono", monospace;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
    }

    .fd-action-text {
      width: 675px;
      max-width: 100%;
      color: #fff;
      font-family: "Roboto Mono", monospace;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
    }
  }

  .fd-doc {
  }

  .fd-video {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16:9
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.38);

    iframe {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  }
}

.fd-card-modal-dialog {
  width: 92vw;
  max-width: 1100px;
  margin: 1.5rem auto;
}

.fd-card-modal-content {
  background: #0f0f0f;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  max-height: 92vh;
}

.fd-card-modal-body {
  position: relative;
  padding: 1rem 1rem 1rem 1rem !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  max-height: 86vh;
  overflow: auto;
  background-color: #191a1a;
}

.fd-card-modal-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.fd-card-modal-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  border-radius: 10px;
  object-fit: contain;
}

.fd-card-modal-close {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  z-index: 2;
}

.fd-card-modal-flip {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  padding: 6px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  z-index: 2;
}

.fd-card-modal-flip-icon {
  width: 22px;
  height: 22px;
  display: block;
}

.fd-card-modal-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: 100%;
  position: relative;
  padding: 0 80px;
  flex-wrap: wrap;
  row-gap: 12px;
  min-height: 44px;
}

.fd-card-modal-nav-center {
  display: inline-flex;
  align-items: center;
  gap: 28px;
}

.fd-card-modal-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: opacity 160ms ease;
}

.fd-card-modal-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.fd-card-modal-nav-btn:focus-visible {
  outline: 2px solid rgba(186, 41, 47, 0.7);
  outline-offset: 4px;
  border-radius: 8px;
}

.fd-card-modal-nav-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.fd-card-modal-nav-text {
  color: #fff;
  text-align: center;
  font-family: "Lacquer";
  font-size: 16.512px;
  font-style: normal;
  font-weight: 400;
  line-height: 16.8px;
}
</style>
